import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/common/Common";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";

export function AuthLayout({ children, title, subtitle }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-blue-50 via-white to-sky-50 border-r border-slate-200">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-md bg-blue-600 text-white flex items-center justify-center font-display font-extrabold">P</div>
          <span className="font-display font-bold text-slate-900">PayMe Contracts</span>
        </div>
        <div className="max-w-md">
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight leading-tight">Onboard, track and pay contractors — calmly.</h1>
          <p className="mt-4 text-slate-500">Contracts, paperwork, rates and payroll handoff for the PayMe team, in one place.</p>
        </div>
        <p className="text-xs text-slate-400">Internal staff system · Australia/Sydney</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-1 mb-6">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, setUser, refreshMe } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={loc.state?.from || "/dashboard"} replace />;

  const done = async (data) => {
    setUser(data);
    await refreshMe();
    navigate(loc.state?.from || "/dashboard", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mfaToken) {
        const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code });
        await done(data);
      } else {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.mfa_required) setMfaToken(data.mfa_token);
        else await done(data);
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={mfaToken ? "Two-step verification" : "Sign in"}
      subtitle={mfaToken ? "Enter the 6-digit code from your authenticator app." : "Use your PayMe staff email. There is no public sign-up."}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {!mfaToken ? (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
            </div>
          </>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="code" className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-blue-600" /> Authentication code</Label>
            <Input id="code" inputMode="numeric" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="font-mono tracking-[0.4em] text-center text-lg" data-testid="login-mfa-code-input" />
          </div>
        )}
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
          {busy && <Spinner className="mr-2" />} {mfaToken ? "Verify" : "Sign in"}
        </Button>
        {mfaToken && (
          <button type="button" className="text-xs text-slate-500 hover:text-slate-800 w-full" onClick={() => { setMfaToken(null); setCode(""); }} data-testid="login-mfa-back">
            Use a different account
          </button>
        )}
      </form>
    </AuthLayout>
  );
}
